import React, { useState, useRef, useEffect, useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown, faXmark } from '@fortawesome/free-solid-svg-icons';
import CheckBox from './CheckBox';
import { useTranslation } from 'react-i18next';

// Lekki, wyszukiwalny multi-select (checkboxy w nakładce). Do dużych list (producenci)
// i małych enumów. value: string[]; onChange(next: string[]).
// `etykieta(opcja)` zwraca tekst do pokazania (np. tłumaczenie wartości z bazy);
// w `value` i w `onChange` zostają zawsze surowe wartości.
const bezZmian = (o) => o;
const MultiSelect = ({ options, value = [], onChange, placeholder, searchable = false, ariaLabel, etykieta = bezZmian }) => {
  const { t } = useTranslation('asystent');
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [wGore, setWGore] = useState(false);
  const ref = useRef(null);
  const panelRef = useRef(null);

  useEffect(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  // Lista otwiera się W GÓRĘ, gdy pod polem nie mieści się w oknie. Przy filtrach
  // stojących nisko w sekcji lista wychodziła poza ekran i trzeba było przewijać
  // stronę, żeby zobaczyć opcje — a przewijanie zamykało kontekst wyboru.
  useEffect(() => {
    if (!open) { setWGore(false); return; }
    const el = ref.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const panel = panelRef.current;
    const wysokosc = panel ? panel.offsetHeight : 280;
    const podSpodem = window.innerHeight - r.bottom;
    const nadSpodem = r.top;
    setWGore(podSpodem < wysokosc + 12 && nadSpodem > podSpodem);
  }, [open]);

  const filtered = useMemo(() => {
    if (!q) return options;
    const s = q.toLowerCase();
    return options.filter((o) => String(etykieta(o)).toLowerCase().includes(s));
  }, [options, q, etykieta]);

  const toggle = (opt) => {
    onChange(value.includes(opt) ? value.filter((v) => v !== opt) : [...value, opt]);
  };

  const summary = value.length === 0
    ? (placeholder ?? t('lista.dowolny'))
    : value.length <= 2
      ? value.map(etykieta).join(', ')
      : t('lista.wybrane', { n: value.length });

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={ariaLabel}
        className="w-full flex items-center gap-2 pl-3 pr-2 py-2 border border-gray-200 rounded-lg text-sm bg-white text-left hover:border-gray-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
      >
        <span className={`flex-1 min-w-0 truncate ${value.length ? 'text-gray-800' : 'text-gray-400'}`}>{summary}</span>
        {value.length > 0 && (
          <span
            role="button"
            tabIndex={0}
            aria-label={t('lista.wyczysc')}
            onClick={(e) => { e.stopPropagation(); onChange([]); }}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.stopPropagation(); onChange([]); } }}
            className="shrink-0 text-gray-400 hover:text-gray-600 cursor-pointer"
          >
            <FontAwesomeIcon icon={faXmark} className="text-xs" />
          </span>
        )}
        <FontAwesomeIcon icon={faChevronDown} className={`shrink-0 text-gray-400 text-xs transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div
          ref={panelRef}
          className={`absolute left-0 right-0 z-30 bg-white border border-gray-200 rounded-lg shadow-xl
                      ${wGore ? 'bottom-full mb-1' : 'top-full mt-1'}`}
        >
          {searchable && (
            <div className="p-2 border-b border-gray-100">
              <input
                type="text"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder={t('lista.szukaj')}
                aria-label={t('lista.szukaj_aria')}
                className="w-full px-2.5 py-1.5 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          )}
          <ul role="listbox" aria-multiselectable="true" className="max-h-56 overflow-y-auto py-1">
            {filtered.length === 0 ? (
              <li className="px-3 py-2 text-sm text-gray-400">{t('lista.brak_wynikow')}</li>
            ) : (
              filtered.map((opt) => {
                const checked = value.includes(opt);
                return (
                  <li key={opt}>
                    <CheckBox checked={checked} onChange={() => toggle(opt)} label={<span className="truncate">{etykieta(opt)}</span>} className="px-3 py-1.5 hover:bg-gray-50" />
                  </li>
                );
              })
            )}
          </ul>
        </div>
      )}
    </div>
  );
};

export default MultiSelect;
